import store from 'redux/store';
import t from 'react-translate';
import { InstitutionLogoSize } from 'redux/schemas/models/institution';
import { openNovoAIDialog } from 'redux/actions/help-options';
import { getCourseById } from 'redux/services/course';
import URLS_TO_HIDE_ASK_NOVO_AI from '../../institutions/components/ask-novoai/helpers';

/* @ngInject */
export default function NvCurrentPage(
  _,
  $state,
  $sce,
  $rootScope,
  $location,
  $cookies,

  CurrentCourseManager,
  CurrentUserManager,
  CurrentPermissionsManager,
  InstitutionsManager,
  PageLevelManager,
  FlyOutPanelManager,
) {
  let modalHeaderCourse;
  let modalPageNoComments = false;
  let trustedHelpWidgetContent = null;

  $rootScope.$on('$stateChangeSuccess', () => {
    trustedHelpWidgetContent = null;
  });

  // Subscribe to Redux store changes for helpOptions
  let previousHelpOptions = null;

  store.subscribe(() => {
    const state = store.getState();
    const currentHelpOptions = state.app?.askNovoAi?.helpOptions;
    // Check if helpOptions changed
    if (previousHelpOptions !== currentHelpOptions) {
      const prevHasAiAssistant = previousHelpOptions?.hasAiLearningAssistantEnabled;
      const prevHasAiEmbedding = previousHelpOptions?.hasAiEmbeddingInProgress;
      const prevHasHelpResources = previousHelpOptions?.hasHelpResourcesEnabled;

      const currentHasAiAssistant = currentHelpOptions?.hasAiLearningAssistantEnabled;
      const currentHasAiEmbedding = currentHelpOptions?.hasAiEmbeddingInProgress;
      const currentHasHelpResources = currentHelpOptions?.hasHelpResourcesEnabled;

      // Reset trustedHelpWidgetContent if either option changed
      if (prevHasAiAssistant !== currentHasAiAssistant || prevHasAiEmbedding !== currentHasAiEmbedding || prevHasHelpResources !== currentHasHelpResources) {
        trustedHelpWidgetContent = null;

        // Trigger Angular digest cycle to update the UI
        if (!$rootScope.$$phase) {
          $rootScope.$apply();
        }
      }

      previousHelpOptions = currentHelpOptions;
    }
  });

  return {
    isCourseHome,
    isJourneyHome,
    isDisplayingState,
    isInstitutionPage,
    isDirectoryPage,
    isViewerAdmin,
    getBrandColor,
    getMainContentBrandColor,
    getFontColor,
    getLanguageClass,
    getLogo,
    hasCourse,
    hasInstitution,
    hasDiscussions,
    hasTeams,
    hasGroups,
    hasGallery,
    hasCommunity,
    hasHelpWidget,
    hasLeaderboard,
    hasLearnersNav,
    hasTeachingTeamNav,
    hasSecondaryQuickAccessOptions,
    getCourse,
    getInstitution,
    getPageLevel,
    getCurrentStateData,
    getNonL4Data,
    getNonL4State,
    getParentState,
    getHelpWidget,
    getTrustedHelpWidgetContent,

    isInProgram,
    getParentProgram,

    isInWalledGarden,
    getCurrentWalledGardenInstitution,

    unreadPrivacyPolicy,
    markUnreadPrivacyPolicyAsRead,
    unreadCookiePolicy,
    markUnreadCookiePolicyAsRead,
    showDowntimeAlert,
    markDowntimeAlertAsRead,
    setModalPageNoComments,
    resetModalPageNoComments,
    modalPageHasNoComments,
    setModalHeaderCourse,
    resetModalHeaderCourse,
    getModalHeaderCourse,
    getModalHeaderCourseLogo,
    getCourseLogoClass,
    uiScrollAdapter: null,

    CurrentPermissionsManager,
  };

  function isCourseHome(includeEditMode = true) {
    // If current state is a modal or flyout, then get parent state
    if (!isModalOrFlyoutPanel()) {
      return CurrentCourseManager.course?.isProgram
        ? $state.includes('program-home') : $state.includes('course-home') || $state.includes('course-home-edit');
    }
    return getNonL4State().name === 'course-home' || getNonL4State().name === 'program-home';
  }

  function isJourneyHome() {
    return $state.includes('learning-journey-home');
  }

  // have to put this here because controllers at the top of the routing chain dont' have access to $stateParams it didn't declare
  function isDisplayingState(stateName) {
    return getCurrentStateData().level !== 4 ? $state.includes(stateName) : getNonL4State().name === stateName;
  }

  function isInstitutionPage() {
    return $state.includes('institution-abstract');
  }

  function isDirectoryPage() {
    return getNonL4Data().isDirectoryPage;
  }

  function isViewerAdmin() {
    return CurrentUserManager.isAdmin();
  }

  /* Brand color for the main-content view. Uses the org branding if an institution page */
  function getMainContentBrandColor() {
    if (PageLevelManager.isLastParentOrgLevel()) {
      return getInstitution().brandColor;
    }

    return getBrandColor();
  }

  function getBrandColor() {
    if (!isInstitutionPage() && hasCourse()) {
      // if course has overriden color, return that, and default to the institution brand color
      return CurrentCourseManager.course.headerColor ? CurrentCourseManager.course.headerColor : CurrentCourseManager.course.institutionBrandColor;
    } if (hasInstitution()) {
      return getInstitution().brandColor;
    }
    return getCurrentWalledGardenInstitution().brandColor;
  }

  function getFontColor() {
    if (hasCourse()) {
      return CurrentCourseManager.course.nameFontColor ? CurrentCourseManager.course.nameFontColor : CurrentCourseManager.course.institutionFontColor;
    } if (hasInstitution()) {
      return getInstitution().brandBarFontColor;
    }
    return getCurrentWalledGardenInstitution().brandBarFontColor;
  }

  /*
      default font: "Open Sans" covers Latin 1, Latin CE, Greek and Cyrillic character sets
      here we only overwrite the default if the language needs a new font stack
      as of 2019-08-28 only Chinese and Japanese needs it's own font stack
    */
  function getLanguageClass() {
    let lang = '';

    // only languages specified in LANGUAGES_WITH_OWN_FONT_STACK have their own font stack. for everything else, we can use the default font stack
    const LANGUAGES_WITH_OWN_FONT_STACK = ['zh_CN', 'ja_JP', 'ko_KP', 'ar_SA'];
    if (hasCourse() && CurrentCourseManager.course.contentLanguage && _.contains(LANGUAGES_WITH_OWN_FONT_STACK, CurrentCourseManager.course.contentLanguage)) {
      lang = CurrentCourseManager.course.contentLanguage;
    } else if (CurrentUserManager.user?.platformLanguage && _.contains(LANGUAGES_WITH_OWN_FONT_STACK, CurrentUserManager.user.platformLanguage)) {
      lang = CurrentUserManager.user.platformLanguage;
    } else if ($cookies.get('platform_language') && _.contains(LANGUAGES_WITH_OWN_FONT_STACK, $cookies.get('platform_language'))) {
      lang = $cookies.get('platform_language');
    }

    if (lang) {
      return `lang-${lang}`;
    }

    return null;
  }

  function getLogo() {
    if (hasCourse() && CurrentCourseManager.course?.logo) {
      return CurrentCourseManager.course.logo.url;
    } if (hasInstitution()) {
      return getInstitution().logo;
    }
    return null;
  }

  function getCourseLogoClass() {
    const LOGO_CLASSES = {
      [InstitutionLogoSize.SMALL]: 'logo-small',
      [InstitutionLogoSize.MEDIUM]: 'logo-medium',
      [InstitutionLogoSize.LARGE]: 'logo-large',
    };

    let logoSize = InstitutionLogoSize.SMALL;
    if (hasCourse()) {
      logoSize = getCourse().logo ? getCourse().logoSize : getInstitution()?.logoSize;
    }

    return LOGO_CLASSES[logoSize];
  }

  function hasCourse() {
    return CurrentCourseManager?.course;
  }

  function hasInstitution() {
    return InstitutionsManager?.institution;
  }

  function hasDiscussions() {
    return hasCourse() && _.indexOf(getCourse().quickAccessOptions, 'Discussions') > -1 && getCourse().numForumsReleased > 0;
  }

  function hasTeams() {
    return hasCourse() && _.indexOf(getCourse().quickAccessOptions, 'Teams') > -1 && getCourse().hasCourseLongTeams;
  }

  function hasGroups() {
    return hasCourse() && _.indexOf(getCourse().quickAccessOptions, 'Groups') > -1 && getCourse().hasGroups;
  }

  function hasCommunity() {
    return hasCourse() && getCourse().hasCommunity;
  }

  function hasGallery() {
    return hasCourse() && _.indexOf(getCourse().quickAccessOptions, 'Gallery') > -1 && getCourse().numFeatureableExercises > 0;
  }

  function hasHelpWidget() {
    const state = store.getState();
    const helpOptions = state.app?.askNovoAi?.helpOptions;
    const showAiAssistant = helpOptions?.hasAiLearningAssistantEnabled || helpOptions?.hasAiEmbeddingInProgress;

    return hasCourse() && (helpOptions?.hasHelpResourcesEnabled || (showAiAssistant && !CurrentCourseManager.course?.inArchiveMode));
  }

  function hasLeaderboard() {
    return hasCourse() && _.indexOf(getCourse().quickAccessOptions, 'Leaderboard') > -1 && getCourse().isLeaderboardEnabled();
  }

  function hasLearnersNav() {
    return hasCourse() && _.indexOf(getCourse().quickAccessOptions, 'Learners') > -1;
  }

  function hasTeachingTeamNav() {
    return hasCourse() && _.indexOf(getCourse().quickAccessOptions, 'Teaching Team') > -1;
  }

  function hasSecondaryQuickAccessOptions() {
    return hasTeams() || hasGroups() || hasLearnersNav() || hasTeachingTeamNav();
  }

  function getCourse() {
    return CurrentCourseManager.course;
  }

  function getInstitution() {
    return InstitutionsManager.institution;
  }

  function isModalOrFlyoutPanel() {
    return $state.current.data.level === 4 || $state.current.data.level === 3.5;
  }

  function getPageLevel() {
    return getNonL4Data().level;
  }

  function getCurrentStateData() {
    return $state.current.data;
  }

  function getNonL4Data() {
    return getNonL4State().data;
  }

  function getNonL4State() {
    // return getCurrentStateData().level !== 4 ? $state.current : PageLevelManager.lastParent().state;
    if (getCurrentStateData().level === 3.5) {
      // Current state is flyout panel get parent
      return FlyOutPanelManager.getParentState();
    }
    if (getCurrentStateData().level === 4) {
      // L4 page. Get Parent
      if (PageLevelManager.lastParent().state && PageLevelManager.lastParent().state.data.level === 3.5) {
        // If parent is an L 3.5 Flyout Panel. Get its flyout panel's parent
        return FlyOutPanelManager.getParentState();
      }
      return PageLevelManager.lastParent().state;
    }
    return $state.current;
  }

  function getParentState() {
    return $state.current.parent;
  }

  function getHelpWidget() {
    const state = store.getState();
    const helpOptions = state.app?.askNovoAi?.helpOptions;
    return hasCourse() && helpOptions?.hasHelpResourcesEnabled && helpOptions?.courseHelp;
  }

  function disableHelpWidgetBasedOnUrl() {
    // Check if the current URL is in the list of URLs to hide AskNovoAI
    if (URLS_TO_HIDE_ASK_NOVO_AI.some(url => $location.path().includes(url))) {
      return false;
    }
    return true;
  }

  function getTrustedHelpWidgetContent() {
    let htmlCode = '';
    if (!trustedHelpWidgetContent) {
      const state = store.getState();
      const helpOptions = state.app?.askNovoAi?.helpOptions;
      const showAiAssistant = (helpOptions?.hasAiLearningAssistantEnabled || helpOptions?.hasAiEmbeddingInProgress) && !CurrentCourseManager.course?.inArchiveMode;
      if (showAiAssistant) {
        const shouldDisableAskNovoAI = !disableHelpWidgetBasedOnUrl();
        htmlCode = `<h3 class="label course-title-xss text-light help-menu-header">${t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.LEARNING_ASSISTANT()}</h3><div class="button-wrapper"><div class="${shouldDisableAskNovoAI ? 'gradient-disabled' : 'gradient-border'}"><button ${shouldDisableAskNovoAI ? 'disabled' : ''} class="novoai-button${shouldDisableAskNovoAI ? ' disabled' : ''}" onclick="window.novoAIButtonClick && window.novoAIButtonClick()"><i class='icon icon-gen-ai${shouldDisableAskNovoAI ? ' icon-gen-ai--disabled' : ''}'></i>${t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.LEARNING_ASSISTANT_BUTTON()}</button></div></div>`;
      }
      if (getHelpWidget() && showAiAssistant) {
        htmlCode += '<div class="bottom-line"></div>';
      }
      if (getHelpWidget()) {
        htmlCode += `<h3 class="label course-title-xss text-light help-menu-header">${getHelpWidget().title}</h3><br/> <div class = "blue-links help-menu-body">${getHelpWidget().body}</div>`;
      }

      if (htmlCode) {
        trustedHelpWidgetContent = $sce.trustAsHtml(htmlCode);
      }
    }

    return trustedHelpWidgetContent;
  }

  function isInProgram() {
    if (hasCourse()) {
      const currentCourse = getCourseById(getCourse().id);
      return currentCourse?.parentPrograms?.length;
    }
    return false;
  }

  function getParentProgram() {
    if (!isInProgram()) {
      return null;
    }
    const currentCourse = getCourseById(getCourse().id);

    // for simplicity we only return the first program
    return currentCourse.parentPrograms[0];
  }

  function isInWalledGarden() {
    return true;
  }

  function getCurrentWalledGardenInstitution() {
    const urlHost = $location.host();

    const currentInstitution = _.find(CurrentUserManager.user.institutions, (walledGardenInst) => walledGardenInst.host === urlHost);

    return currentInstitution;
  }

  function unreadPrivacyPolicy() {
    return _.find(CurrentUserManager.user.unreadLegalTrackables, (trackable) => trackable.type === 'PrivacyPolicy'
              && (!trackable.institutionId || (CurrentUserManager.currentInstitution && trackable.institutionId === CurrentUserManager.currentInstitution.id)));
  }

  function unreadCookiePolicy() {
    return _.find(CurrentUserManager.user.unreadLegalTrackables, (trackable) => trackable.type === 'CookiePolicy');
  }

  function markUnreadPrivacyPolicyAsRead() {
    const unreadPolicy = unreadPrivacyPolicy();
    if (unreadPolicy) {
      return CurrentUserManager.removeUnreadTrackable(unreadPolicy.id);
    }
    return null;
  }

  function markUnreadCookiePolicyAsRead() {
    const unreadPolicy = unreadCookiePolicy();
    if (unreadPolicy) {
      return CurrentUserManager.removeUnreadTrackable(unreadPolicy.id);
    }
    return null;
  }

  function showDowntimeAlert() {
    return CurrentUserManager.user.mustSeeDowntimeAlert;
  }

  function markDowntimeAlertAsRead() {
    return CurrentUserManager.markDowntimeAlertAsRead();
  }

  function setModalPageNoComments() {
    modalPageNoComments = true;
  }

  function resetModalPageNoComments() {
    modalPageNoComments = false;
  }

  function modalPageHasNoComments() {
    return !!modalPageNoComments;
  }

  function setModalHeaderCourse(course) {
    modalHeaderCourse = course;
  }

  function resetModalHeaderCourse() {
    modalHeaderCourse = null;
  }

  function getModalHeaderCourse() {
    return modalHeaderCourse;
  }

  function getModalHeaderCourseLogo() {
    if (modalHeaderCourse) {
      if (modalHeaderCourse.thumbnail && !modalHeaderCourse.thumbnail.includes('assets/brand/temp.png')) {
        return modalHeaderCourse.thumbnail;
      }
      return InstitutionsManager.institution.logo;
    }

    return null;
  }
}

// Attach the handler to window so it can be called from the button
if (typeof window !== 'undefined') {
  window.novoAIButtonClick = () => {
    store.dispatch(openNovoAIDialog());
  };
}
